import { useEffect, useState } from 'react';
import { useAuthStore } from '@/hooks/useAuthStore';
import { isDemoUser, exitDemo, clearSandbox } from '@/services/demoSandbox';

/**
 * DEMO-ONLY persona switcher + sandbox banner. Shows automatically when a demo
 * account is logged in (or when ?demo=1 is set for manual testing). Lets a
 * prospect view each access level and play in a local-only sandbox — writes
 * never reach the DB (see demoSandbox). Backend permissions are unaffected; run
 * demos on an owner/admin demo account so the views render fully.
 */
const FLAG = 'hazeasy_demo';
const SNAP = 'hazeasy_demo_orig';

type Persona = { key: string; label: string; role?: string; accountType?: string; disabled?: boolean };
const PERSONAS: Persona[] = [
  { key: 'field_tech', label: 'Field Tech', role: 'field_tech', accountType: 'broker' },
  { key: 'office', label: 'Office / Admin', role: 'owner', accountType: 'broker' },
  { key: 'generator', label: 'Generator (coming)', disabled: true },
];

function demoEnabled(): boolean {
  if (typeof window === 'undefined') return false;
  if (new URLSearchParams(window.location.search).has('demo')) localStorage.setItem(FLAG, '1');
  return isDemoUser() || localStorage.getItem(FLAG) === '1';
}

export default function DemoPersonaSwitcher() {
  const [enabled] = useState(demoEnabled);
  const [active, setActive] = useState('field_tech');
  const user = useAuthStore((s) => s.user);

  useEffect(() => {
    if (!enabled || !user) return;
    if (!sessionStorage.getItem(SNAP)) {
      const s = useAuthStore.getState();
      sessionStorage.setItem(SNAP, JSON.stringify({ role: s.user?.role, accountType: s.organization?.accountType }));
      apply('field_tech');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, !!user]);

  if (!enabled || !user) return null;

  function apply(key: string) {
    const p = PERSONAS.find((x) => x.key === key);
    if (!p || p.disabled) return;
    useAuthStore.setState((s) => ({
      user: s.user ? { ...s.user, role: p.role as string } : s.user,
      organization: s.organization ? { ...s.organization, accountType: p.accountType as any } : s.organization,
    }));
    setActive(key);
  }

  function restorePersona() {
    try {
      const snap = JSON.parse(sessionStorage.getItem(SNAP) || '{}');
      useAuthStore.setState((s) => ({
        user: s.user ? { ...s.user, role: snap.role ?? s.user.role } : s.user,
        organization: s.organization ? { ...s.organization, accountType: snap.accountType ?? s.organization.accountType } : s.organization,
      }));
    } catch { /* noop */ }
    sessionStorage.removeItem(SNAP);
  }

  function reset() {
    clearSandbox();
    window.location.reload();
  }
  function exit() {
    restorePersona();
    exitDemo();
    localStorage.removeItem(FLAG);
    window.location.reload();
  }

  return (
    <div className="fixed top-0 left-0 right-0 z-[9999] flex items-center gap-2 px-3 py-1 text-xs bg-amber-400 text-black shadow">
      <span className="font-semibold">DEMO — view as:</span>
      {PERSONAS.map((p) => (
        <button
          key={p.key}
          disabled={p.disabled}
          onClick={() => apply(p.key)}
          className={`px-2 py-0.5 rounded font-medium transition-colors ${
            active === p.key ? 'bg-black text-amber-400' : 'bg-amber-200 hover:bg-amber-300'
          } ${p.disabled ? 'opacity-40 cursor-not-allowed' : ''}`}
        >
          {p.label}
        </button>
      ))}
      <span className="ml-2 hidden sm:inline opacity-70">sandbox — changes stay on this device</span>
      <button onClick={reset} className="ml-auto underline hover:no-underline">reset sandbox</button>
      <button onClick={exit} className="underline hover:no-underline">exit</button>
    </div>
  );
}
